import isEqual from 'lodash-es/isEqual';
import {
  Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';

import { isEmpty } from '@app/util/util';
import {
  DropDownListDefaultValues, DropDownListOption, DropDownListPagination, DropDownListPaginator, DropDownListSelectedBy
} from '@design-system/components/templates/drop-down-list/entities/drop-down-list.types';
import { RadioButtonText } from '@design-system/components/organisms/radio-button/entities/radio-button.types';
import { ImageSize } from '@design-system/components/molecules/image/entities/image.types';
import { Icon, IconFillColor } from '@design-system/components/atoms/icon/entities/icon.types';
import { TypographyFontFamily, TypographyFontSize } from '@design-system/components/atoms/typography/entities/typography.types';
import { InputSearchSize } from '@design-system/components/molecules/input-search/entities/input-search.types';
import { BorderRadiusDsType } from '@design-system/entities/border-radius.type';
import {
  dropDownListPaginator
} from '@design-system/components/templates/drop-down-list/mappers/drop-down-list-paginator.mapper';
import { DividerDsComponent } from '@design-system/components/atoms/divider/divider.component';
import { IconDsComponent } from '@design-system/components/atoms/icon/icon.component';
import { TypographyDsComponent } from '@design-system/components/atoms/typography/typography.component';
import { ImageDsComponent } from '@design-system/components/molecules/image/image.component';
import { InputSearchDsComponent } from '@design-system/components/molecules/input-search/input-search.component';
import { RadioButtonDsComponent } from '@design-system/components/organisms/radio-button/radio-button.component';

@Component({
  selector: 'app-ds-drop-down-list',
  templateUrl: './drop-down-list.component.html',
  styleUrls: ['./drop-down-list.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    InfiniteScrollDirective,
    ImageDsComponent,
    RadioButtonDsComponent,
    InputSearchDsComponent,
    IconDsComponent,
    TypographyDsComponent,
    DividerDsComponent,
  ]
})
export class DropDownListDsComponent implements OnChanges {

  @Input() type = DropDownListDefaultValues.Type;
  @Input() selectedBy = DropDownListDefaultValues.SelectedBy;
  @Input() options: DropDownListOption[] = [];
  @Input() selectedId: string;
  @Input() searchInput = false;
  @Input() searchInputPlaceholder = '';
  @Input() borderRadius: BorderRadiusDsType = null;
  @Input() resetSearch = false;
  @Input() infiniteScrollDisabled = true;

  @Output() selectEvent: EventEmitter<DropDownListOption> = new EventEmitter<DropDownListOption>();
  @Output() searched: EventEmitter<DropDownListOption[]> = new EventEmitter<DropDownListOption[]>();

  public readonly imageSize = ImageSize.MD;

  public readonly iconFillColor = IconFillColor.Primary;
  public readonly check = Icon.Check;

  public readonly fontSizeXXS = TypographyFontSize.XXS;
  public readonly fontFamilySemiBold = TypographyFontFamily.SemiBold;

  public readonly inputSearchSize = InputSearchSize.MD;

  public querySearch: string;
  public slice = 0;

  private paginator: DropDownListPaginator;
  private optionsByQuery: DropDownListOption[] = [];
  private currentPage = 1;
  private endPage = 1;

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.resetSearch?.currentValue && this.searchInput) {
      this.search('');
    }
    if (changes?.options || changes?.infiniteScrollDisabled) {
      if (
        !isEqual(changes?.options?.currentValue, changes?.options?.previousValue)
      ) {        
        this.paginator = dropDownListPaginator(
          this.viewOptions.length, 1, DropDownListPagination.DefaultLimit, DropDownListPagination.DefaultMaxPage
        );
        this.endPage = this.paginator.EndPage;
      }
      if (changes?.infiniteScrollDisabled?.currentValue) {
        this.slice = this.viewOptions?.length;
      } else {
        this.slice = DropDownListPagination.DefaultLimit * this.currentPage;
      }
    }
  }

  get dropDownListSelectedBy(): typeof DropDownListSelectedBy {
    return DropDownListSelectedBy;
  }

  get viewOptions(): DropDownListOption[] {
    return !isEmpty(this.querySearch) ? this.optionsByQuery : this.options;
  }

  public optionIsSelected(option: DropDownListOption): boolean {
    return !isEmpty(this.selectedId) && option?.Id === this.selectedId;
  }

  public getRadioTitle(option: DropDownListOption): RadioButtonText {
    return { Label: option?.Title };
  }

  public getRadioSubTitle(option: DropDownListOption): RadioButtonText[] {
    return option?.Subtitle ? [{ Label: option.Subtitle }] : [];
  }

  public trackById(index: number): number {
    return index;
  }

  public search(query: string): void {
    this.querySearch = query;
    if (isEmpty(this.querySearch)) {
      this.optionsByQuery = [];
    } else {
      this.optionsByQuery = this.options.filter(option => option?.Title?.toUpperCase()?.includes(query?.toUpperCase()))
    }
    this.resetPaginator();
    this.searched.emit(this.viewOptions);
  }

  public scrolled(): void {
    if (!this.infiniteScrollDisabled && this.currentPage < this.endPage) {
      this.currentPage += 1;
      this.slice = DropDownListPagination.DefaultLimit * this.currentPage;
    }
  }

  private resetPaginator(): void {
    if (!this.infiniteScrollDisabled) {
      if (this.viewOptions) {
        this.paginator = dropDownListPaginator(
          this.viewOptions.length, 1, DropDownListPagination.DefaultLimit, DropDownListPagination.DefaultMaxPage
        );
        this.endPage = this.paginator.EndPage;
        this.currentPage = this.paginator.CurrentPage;
        this.slice = DropDownListPagination.DefaultLimit * this.currentPage;
      } else {
        this.currentPage = 1;
        this.slice = DropDownListPagination.DefaultLimit * this.currentPage;
      }
    }
  }

}
